import React, { Dispatch, useEffect, useLayoutEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { fetchHistoricalApplicationsInfo } from '@actions/historicalMemberApplication/historicalApplicationsActionCreator';
import { isHistoricalYear } from '@modules/historical-application/utils/ApplicationContainerUtils';
import { HistoricalApplicationsContent } from '@modules/members/historical-applications/components';
import { getEncHouseholdId } from '../../utils/storageUtils';

import {
    ConditionalRendering,
    useAppSelector,
    useAppDispatch,
    fetchApplicationsDetails,
    setCurrentYear,
    environmentSelector,
} from '../../utils';
import YearSelector from '../../common/yearSelector';
import ApplicationsList from '../applicationsList/ApplicationsList';
import {
    StyledLabel,
    StyledHeader,
    StyledLabelNoData,
    StyledContainer,
} from './MemberApplicationsListContainer.styles';

function MemberApplicationsListContainer() {
    const [isLoading, setIsLoading] = useState(true);
    const dispatch: Dispatch<any> = useAppDispatch();
    const environment = useAppSelector(environmentSelector);
    const { exchangeStartYear, flags } = environment;
    const showHistoricalData = flags?.showHistoricalData || false;
    const currentYear = useAppSelector(state => state.capApplications.currentYear);
    const { currentApplications, pastApplications, queuedApplications } = useAppSelector(
        state => state.capApplications.capApplicationsInfo,
    );
    const { appCoverageYears, currentCoverageYear } = useAppSelector(state => state.capApplications.appControllerInfo);
    const hasCurrentApplicationsData = currentApplications.length === 0 && pastApplications.length > 0;
    const hasCurrentPastQueuedAppData =
        currentApplications.length === 0 && pastApplications.length === 0 && queuedApplications.length === 0;

    const currentApplicationYear = currentYear || currentCoverageYear;
    const householdId = getEncHouseholdId();
    const { historicalApplicationsInfo } = useAppSelector(state => state.historicalApplications);
    const historicalApplicationsData = historicalApplicationsInfo?.myApplications || [];
    const isSelectedYearHistorical = showHistoricalData && isHistoricalYear(Number(currentApplicationYear), exchangeStartYear);
    const loadingCapList = Boolean(currentApplicationYear) && !isSelectedYearHistorical;

    // Set loading before paint when CAP year changes so empty lists from the last response do not flash "No data available".
    // Include currentApplicationYear so switching between two non-historical years resets loading (loadingCapList stays true).
    useLayoutEffect(() => {
        setIsLoading(loadingCapList);
    }, [loadingCapList, currentApplicationYear]);

    useEffect(() => {
        if (currentApplicationYear) {
            if (isSelectedYearHistorical) {
                if (householdId) {
                    dispatch(fetchHistoricalApplicationsInfo(currentApplicationYear, '', householdId));
                }
            } else {
                // Second argument is unused by fetchApplicationsDetails; do not list `environment` in deps —
                // a new object reference would refetch without useLayoutEffect re-setting loading.
                dispatch(
                    fetchApplicationsDetails(currentApplicationYear, undefined, () => {
                        setIsLoading(false);
                    }),
                );
            }
        }
    }, [currentApplicationYear, isSelectedYearHistorical, dispatch, householdId]);

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(setCurrentYear(event.target.value));
    };

    if (loadingCapList && isLoading) {
        return null;
    }

    return (
        <StyledContainer className="desktop:grid-col-9 member-applications-container" id="main-content">
            <StyledHeader className="header">
                <StyledLabel>
                    <ConditionalRendering isTrue={isSelectedYearHistorical}>
                        <FormattedMessage id="historical.member.applications.applications" values={{ currentYear: currentApplicationYear }} />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={loadingCapList}>
                        <FormattedMessage id="cap.applications.header.currentApplications" />
                    </ConditionalRendering>
                </StyledLabel>
                <YearSelector
                    titleId="cap.applications.header.applicationYear"
                    years={appCoverageYears}
                    currentYear={currentApplicationYear}
                    onYearChange={handleYearChange}
                />
            </StyledHeader>
            <ConditionalRendering isTrue={isSelectedYearHistorical}>
                <div className="padding-3">
                    <HistoricalApplicationsContent applications={historicalApplicationsData} />
                </div>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!isSelectedYearHistorical}>
                <ConditionalRendering isTrue={hasCurrentApplicationsData}>
                    <ApplicationsList applications={currentApplications} applicationListId="no-current-application" />
                    <StyledLabelNoData>
                        <FormattedMessage id="cap.applications.message.noCurrentAppData" />
                    </StyledLabelNoData>
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentApplications?.length > 0}>
                    <ApplicationsList applications={currentApplications} applicationListId="current-application" />
                </ConditionalRendering>

                <ConditionalRendering isTrue={hasCurrentPastQueuedAppData && Boolean(currentApplicationYear)}>
                    <StyledLabelNoData>
                        <FormattedMessage id="cap.applications.message.noData" />
                    </StyledLabelNoData>
                </ConditionalRendering>

                <ConditionalRendering isTrue={pastApplications?.length > 0}>
                    <StyledHeader className="header">
                        <StyledLabel>
                            <FormattedMessage id="cap.applications.header.pastApplications" />
                        </StyledLabel>
                        <span>
                            <FormattedMessage id="cap.applications.header.applicationYear" />
                            <label className="margin-l-5">{currentApplicationYear}</label>
                        </span>
                    </StyledHeader>
                    <ApplicationsList applications={pastApplications} applicationListId="past-application" />
                </ConditionalRendering>

                <ConditionalRendering isTrue={queuedApplications?.length > 0}>
                    <StyledHeader className="header">
                        <StyledLabel>
                            <FormattedMessage id="cap.applications.header.queuedApplications" />
                        </StyledLabel>
                        <YearSelector titleId="cap.applications.header.applicationYear" currentYear={currentApplicationYear} />
                    </StyledHeader>
                    <ApplicationsList applications={queuedApplications} applicationListId="queued-application" />
                </ConditionalRendering>
            </ConditionalRendering>

        </StyledContainer>
    );
}

export default MemberApplicationsListContainer;
